import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import StatCard from '../components/common/StatCard';
import { SkeletonDashboard } from '../components/common/Skeleton';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { QK } from '../queryKeys';

const FUNNEL_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#6366f1', '#ef4444'];

const SR_PRIORITY_COLORS = { urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#10b981' };
const SR_STATUS_COLORS = { open: '#3b82f6', in_progress: '#8b5cf6', pending_customer: '#f59e0b', resolved: '#10b981', closed: '#64748b', cancelled: '#94a3b8' };

const ClientsIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const SalesIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 14l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);
const CatalogIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);
const InquiryIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);
const ProjectIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <path d="M8 21h8M12 17v4"/>
    <path d="M7 8h.01M11 8h6"/>
  </svg>
);
const ServiceIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"/>
    <path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    <path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"/>
    <path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"/>
    <path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z"/>
    <path d="M15.5 19H14v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z"/>
    <path d="M10 9.5C10 8.67 9.33 8 8.5 8h-5C2.67 8 2 8.67 2 9.5S2.67 11 3.5 11h5c.83 0 1.5-.67 1.5-1.5z"/>
    <path d="M8.5 5H10V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5z"/>
  </svg>
);
const PurchaseIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="8.5" cy="7" r="4"/>
    <path d="M20 8l-4 4m0-4l4 4"/>
  </svg>
);
const OrderIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);
const WonIcon = (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

// fmtCurrency replaced by useCompany().fmtCompact at call sites

const statusBadge = (s) => {
  const map = { won: 'success', lost: 'danger', quoted: 'warning', new: 'primary', negotiating: 'info', on_hold: 'neutral' };
  return map[s] || 'neutral';
};

export default function Dashboard() {
  const { user, isPrivileged } = useAuth();
  const { fmtCompact } = useCompany();
  const navigate = useNavigate();

  const { data: dashData, isLoading: loading, error: dashError } = useQuery({
    queryKey: QK.dashboard(),
    queryFn: async () => {
      const baseRequests = [
        api.get('/dashboard/stats'),
        api.get('/dashboard/sales-chart'),
        api.get('/dashboard/inquiry-funnel'),
        api.get('/dashboard/recent-inquiries'),
        api.get('/dashboard/active-projects'),
        api.get('/dashboard/top-catalog'),
        api.get('/dashboard/recent-service-requests'),
      ];
      const [s, sc, f, ri, ap, tc, sr] = await Promise.all(baseRequests);

      // Alert widgets (fire-and-forget, best effort)
      const [licenseRes, activitiesRes, stalledRes, tvaRes] = await Promise.all([
        api.get('/clients/license-expiry', { params: { days: 30 } }).catch(() => null),
        api.get('/activities', { params: { status: 'pending' } }).catch(() => null),
        api.get('/dashboard/stalled-deals', { params: { days: 7 } }).catch(() => null),
        api.get('/tva', { params: { year: new Date().getFullYear(), month: new Date().getMonth() + 1 } }).catch(() => null),
      ]);

      const licenseRows = licenseRes ? (licenseRes.data.data || licenseRes.data || []) : [];
      const activityRows = activitiesRes ? (activitiesRes.data.data || activitiesRes.data || []) : [];

      let hrStats = null;
      let payrollSummary = null;
      if (isPrivileged) {
        const currentMonth = new Date().toISOString().slice(0, 7);
        const [hr, pr] = await Promise.all([
          api.get('/hr/stats').catch(() => null),
          api.get(`/hr/payroll?month=${currentMonth}`).catch(() => null),
        ]);
        if (hr) hrStats = hr.data.data;
        if (pr) {
          const rows = pr.data.data || [];
          const processed = rows.filter(r => r.payroll_id);
          const paid = rows.filter(r => r.status === 'paid');
          const totalNet = paid.reduce((sum, r) => sum + parseFloat(r.net_pay || 0), 0);
          payrollSummary = { total: rows.length, processed: processed.length, paid: paid.length, totalNet, month: currentMonth };
        }
      }

      return {
        stats: s.data.data,
        salesChart: sc.data.data || [],
        funnel: f.data.data || [],
        recentInquiries: ri.data.data || [],
        activeProjects: ap.data.data || [],
        topCatalog: tc.data.data || [],
        recentSR: sr.data.data || [],
        expiringLicenses: Array.isArray(licenseRows) ? licenseRows.length : 0,
        overdueActivities: Array.isArray(activityRows) ? activityRows.length : 0,
        stalledDeals: stalledRes ? (stalledRes.data.data || []) : [],
        tvaData: tvaRes ? tvaRes.data : null,
        hrStats,
        payrollSummary,
      };
    },
    refetchInterval: 5 * 60 * 1000, // refresh every 5 minutes
  });

  const stats = dashData?.stats ?? null;
  const salesChart = dashData?.salesChart ?? [];
  const funnel = dashData?.funnel ?? [];
  const recentInquiries = dashData?.recentInquiries ?? [];
  const activeProjects = dashData?.activeProjects ?? [];
  const topCatalog = dashData?.topCatalog ?? [];
  const recentSR = dashData?.recentSR ?? [];
  const hrStats = dashData?.hrStats ?? null;
  const payrollSummary = dashData?.payrollSummary ?? null;
  const expiringLicenses = dashData?.expiringLicenses ?? null;
  const overdueActivities = dashData?.overdueActivities ?? null;
  const tvaData = dashData?.tvaData ?? null;
  const stalledDeals = dashData?.stalledDeals ?? null;

  const statCards = [
    {
      key: 'clients',
      label: isPrivileged ? 'Total Clients' : 'My Clients',
      value: stats?.total_clients ?? 0,
      subtitle: `${stats?.active_clients ?? 0} active`,
      color: 'blue',
      icon: ClientsIcon,
      onClick: () => navigate('/admin/clients'),
    },
    ...(isPrivileged ? [
      {
        key: 'sales',
        label: 'Total Sales',
        value: stats?.total_sales ?? 0,
        subtitle: `${fmtCompact(stats?.total_revenue)} revenue`,
        color: 'blue',
        icon: SalesIcon,
        onClick: () => navigate('/admin/orders'),
      },
      {
        key: 'catalog',
        label: 'Catalog Items',
        value: stats?.catalog_items ?? 0,
        subtitle: `${stats?.in_stock ?? 0} in stock`,
        color: 'green',
        icon: CatalogIcon,
        onClick: () => navigate('/admin/catalog'),
      },
    ] : [
      {
        key: 'orders',
        label: 'My Orders',
        value: stats?.total_orders ?? 0,
        subtitle: `${fmtCompact(stats?.total_revenue)} revenue`,
        color: 'green',
        icon: OrderIcon,
        onClick: () => navigate('/admin/orders'),
      },
      {
        key: 'catalog',
        label: 'Catalog Items',
        value: stats?.catalog_items ?? 0,
        subtitle: `${stats?.in_stock ?? 0} in stock`,
        color: 'green',
        icon: CatalogIcon,
        onClick: () => navigate('/admin/catalog'),
      },
    ]),
    {
      key: 'inquiries',
      label: isPrivileged ? 'Open Inquiries' : 'My Open Inquiries',
      value: stats?.open_inquiries ?? 0,
      subtitle: `${stats?.won_this_month ?? 0} won this month`,
      color: 'orange',
      icon: InquiryIcon,
      onClick: () => navigate('/admin/inquiries'),
    },
    ...(isPrivileged ? [
      {
        key: 'projects',
        label: 'Active Projects',
        value: stats?.active_projects ?? 0,
        subtitle: `${fmtCompact(stats?.pipeline_value)} pipeline`,
        color: 'purple',
        icon: ProjectIcon,
        onClick: () => navigate('/admin/projects'),
      },
    ] : [
      {
        key: 'won',
        label: 'Won This Month',
        value: stats?.won_this_month ?? 0,
        subtitle: `${fmtCompact(stats?.pipeline_value)} pipeline`,
        color: 'orange',
        icon: WonIcon,
        onClick: () => navigate('/admin/inquiries'),
      },
    ]),
    {
      key: 'service',
      label: 'Service Requests',
      value: stats?.open_sr ?? 0,
      subtitle: `${stats?.urgent_sr ?? 0} urgent`,
      color: 'red',
      icon: ServiceIcon,
      onClick: () => navigate('/admin/service-requests'),
    },
    ...(isPrivileged ? [
      {
        key: 'total_purchase',
        label: 'Total Purchase',
        value: stats?.total_purchase ?? 0,
        subtitle: `${stats?.closed_purchase ?? 0} closed`,
        color: 'orange',
        icon: PurchaseIcon,
        onClick: () => navigate('/admin/orders?order_type=purchase_order'),
      },
      {
        key: 'open_purchase',
        label: 'Open Purchase',
        value: stats?.open_purchase ?? 0,
        subtitle: 'Non-closed orders',
        color: 'yellow',
        icon: PurchaseIcon,
        onClick: () => navigate('/admin/orders?order_type=purchase_order&status=open'),
      },
    ] : []),
  ];

  if (loading) return <SkeletonDashboard />;

  return (
    <div style={{ paddingTop: 24, paddingBottom: 40, padding: '0 24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Stats */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        {statCards.map(card => (
          <StatCard
            key={card.key}
            label={card.label}
            value={card.value}
            color={card.color}
            subtitle={card.subtitle}
            icon={card.icon}
            onClick={card.onClick}
          />
        ))}
      </div>

      {/* Alert Widgets */}
      {(expiringLicenses !== null || overdueActivities !== null) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {expiringLicenses !== null && (
            <div
              onClick={() => navigate('/admin/license-expiry')}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, cursor: 'pointer',
                background: expiringLicenses > 0 ? '#fffbeb' : '#f0fdf4',
                border: `1px solid ${expiringLicenses > 0 ? '#fcd34d' : '#bbf7d0'}`,
                minWidth: 220,
              }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: expiringLicenses > 0 ? '#d97706' : '#059669' }}>
                {expiringLicenses}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: expiringLicenses > 0 ? '#92400e' : '#065f46' }}>
                  Expiring Licenses
                </div>
                <div style={{ fontSize: '0.72rem', color: expiringLicenses > 0 ? '#b45309' : '#047857' }}>
                  {expiringLicenses > 0 ? 'Within next 30 days — click to review' : 'No licenses expiring soon'}
                </div>
              </div>
            </div>
          )}
          {overdueActivities !== null && (
            <div
              onClick={() => navigate('/admin/activities')}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, cursor: 'pointer',
                background: overdueActivities > 0 ? '#fff1f2' : '#f0fdf4',
                border: `1px solid ${overdueActivities > 0 ? '#fca5a5' : '#bbf7d0'}`,
                minWidth: 220,
              }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: overdueActivities > 0 ? '#dc2626' : '#059669' }}>
                {overdueActivities}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: overdueActivities > 0 ? '#991b1b' : '#065f46' }}>
                  Pending Activities
                </div>
                <div style={{ fontSize: '0.72rem', color: overdueActivities > 0 ? '#b91c1c' : '#047857' }}>
                  {overdueActivities > 0 ? 'Activities awaiting completion — click to review' : 'No pending activities'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stalled Deals Widget */}
      {stalledDeals !== null && stalledDeals.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #f59e0b' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" fill="none" stroke="#f59e0b" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Stalled Deals — {stalledDeals.length} inquir{stalledDeals.length === 1 ? 'y' : 'ies'} with no update in 7+ days
            </span>
            <button onClick={() => navigate('/admin/inquiries')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              View All Inquiries →
            </button>
          </div>
          <div className="card-body" style={{ padding: '0 0 4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#92400e' }}>Inquiry</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#92400e' }}>Client</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#92400e' }}>Status</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#92400e' }}>Assigned</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#92400e' }}>Idle Days</th>
                </tr>
              </thead>
              <tbody>
                {stalledDeals.slice(0, 5).map(d => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onClick={() => navigate('/admin/inquiries')}
                    onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '8px 16px', color: '#1d4ed8', fontWeight: 600 }}>{d.inquiry_number}</td>
                    <td style={{ padding: '8px 16px', color: '#374151' }}>{d.client_name || '—'}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                        background: { new:'#dbeafe', quoted:'#fef3c7', negotiating:'#ede9fe', on_hold:'#f3f4f6' }[d.status] || '#f3f4f6',
                        color: { new:'#1d4ed8', quoted:'#92400e', negotiating:'#5b21b6', on_hold:'#374151' }[d.status] || '#374151' }}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 16px', color: '#6b7280' }}>{d.assigned_to_name || '—'}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: d.stale_days >= 14 ? '#dc2626' : '#d97706' }}>
                      {d.stale_days}d
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TVA Summary */}
      {tvaData && tvaData.data && tvaData.data.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">
              TVA — {new Date(tvaData.year, tvaData.month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => navigate('/admin/tva')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View TVA →</button>
          </div>
          <div className="card-body" style={{ padding: '12px 20px' }}>
            {(isPrivileged ? tvaData.data : tvaData.data.filter(r => r.user_id === user?.id)).map(row => {
              const salesPct = row.target_amount > 0 ? Math.min(100, Math.round((row.achieved_amount / row.target_amount) * 100)) : null;
              const visitsPct = row.target_visits > 0 ? Math.min(100, Math.round((row.achieved_visits / row.target_visits) * 100)) : null;
              const custPct = row.target_new_customers > 0 ? Math.min(100, Math.round((row.achieved_new_customers / row.target_new_customers) * 100)) : null;
              return (
                <div key={row.user_id} style={{ marginBottom: 14 }}>
                  {isPrivileged && (
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                      {row.user_name} <span style={{ fontWeight: 400, color: 'var(--text-muted)', textTransform: 'capitalize' }}>({row.role})</span>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {[
                      { label: 'Sales Achievement', pct: salesPct, achieved: fmtCompact(row.achieved_amount), target: fmtCompact(row.target_amount), color: '#3b82f6' },
                      { label: 'Visits', pct: visitsPct, achieved: row.achieved_visits, target: row.target_visits, color: '#10b981' },
                      { label: 'New Customers', pct: custPct, achieved: row.achieved_new_customers, target: row.target_new_customers, color: '#8b5cf6' },
                    ].map(({ label, pct, achieved, target, color }) => (
                      <div key={label} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
                        <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                          <div style={{ height: '100%', width: `${pct ?? 0}%`, background: color, borderRadius: 3, transition: 'width 0.4s' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{achieved}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{pct !== null ? `${pct}%` : '—'} of {target}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">{isPrivileged ? 'Revenue — Last 6 Months' : 'My Revenue — Last 6 Months'}</span>
          </div>
          <div className="card-body" style={{ padding: '10px 12px 16px' }}>
            {salesChart.every(m => m.revenue === 0) ? (
              <div style={{ height: 200, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize: '0.875rem' }}>No revenue data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={salesChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`}/>
                  <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}/>
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#revGrad)" dot={{ r: 3, fill: '#3b82f6' }}/>
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">{isPrivileged ? 'Inquiry Pipeline' : 'My Inquiry Pipeline'}</span>
          </div>
          <div className="card-body" style={{ padding: '10px 12px 16px' }}>
            {funnel.every(f => f.count === 0) ? (
              <div style={{ height: 200, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize: '0.875rem' }}>No inquiries yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={funnel} layout="vertical" margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false}/>
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                  <YAxis type="category" dataKey="status" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={85}
                    tickFormatter={s => s.charAt(0).toUpperCase() + s.slice(1)}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}/>
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {funnel.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Revenue Breakdown</span>
        </div>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          {/* Main Revenue Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>TOTAL REVENUE</div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-primary)' }}>{"\u20b9"}{fmtCompact(stats?.total_revenue || 0)}</div>
            </div>
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Amount Without GST</div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-primary)' }}>{"\u20b9"}{fmtCompact(Math.round((stats?.total_revenue || 0) / 1.18))}</div>
            </div>
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>GST Amount (18%)</div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-primary)' }}>{"\u20b9"}{fmtCompact(Math.round((stats?.total_revenue || 0) * 0.18 / 1.18))}</div>
            </div>
          </div>

          {/* Individual Amounts Section */}
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12 }}>Individual Amounts</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div style={{ background: 'var(--surface-1)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{"\u20b9"}{fmtCompact(stats?.total_sales || 0)}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Sales Total</div>
              </div>
              <div style={{ background: 'var(--surface-1)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{"\u20b9"}{fmtCompact(stats?.total_purchase || 0)}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Purchase Total</div>
              </div>
              <div style={{ background: 'var(--surface-1)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{stats?.pending_orders || 0}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>Pending Orders</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">{isPrivileged ? 'Recent Inquiries' : 'My Recent Inquiries'}</span>
            <button onClick={() => navigate('/admin/inquiries')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View All →</button>
          </div>
          <div>
            {recentInquiries.length === 0 ? (
              <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No inquiries yet</div>
            ) : recentInquiries.map(inq => (
              <div key={inq.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--primary)' }}>{inq.inquiry_number}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inq.client_name}</div>
                </div>
                <span className={`badge badge-${statusBadge(inq.status)}`} style={{ textTransform: 'capitalize', flexShrink: 0, fontSize: '0.7rem' }}>
                  {inq.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">{isPrivileged ? 'Active Projects' : 'My Active Projects'}</span>
            <button onClick={() => navigate('/admin/projects')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View All →</button>
          </div>
          <div>
            {activeProjects.length === 0 ? (
              <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No active projects</div>
            ) : activeProjects.map(p => (
              <div key={p.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--primary)' }}>{p.project_code}</div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#22c55e', flexShrink: 0 }}>{p.status}</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name}</div>
                {p.budget && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>{fmtCompact(p.budget)}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">{isPrivileged ? 'Top Catalog Items' : 'My Top Products'}</span>
            <button onClick={() => navigate('/admin/catalog')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View All →</button>
          </div>
          <div>
            {topCatalog.length === 0 ? (
              <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No orders yet</div>
            ) : topCatalog.map((item, i) => (
              <div key={item.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ overflow: 'hidden', flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>CAS: {item.cas_number || '—'} · {item.order_count} orders</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">Recent Service Requests</span>
            <button onClick={() => navigate('/admin/service-requests')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View All →</button>
          </div>
          <div>
            {recentSR.length === 0 ? (
              <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No service requests</div>
            ) : recentSR.map(sr => (
              <div key={sr.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sr.sr_number}</div>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px', borderRadius: 20, flexShrink: 0,
                    background: SR_PRIORITY_COLORS[sr.priority] + '18', color: SR_PRIORITY_COLORS[sr.priority] || '#64748b' }}>
                    {sr.priority}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sr.title}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
                  {sr.client_name || sr.customer_name || '—'}
                  {sr.assigned_to_name && <span style={{ color: '#94a3b8' }}> · {sr.assigned_to_name.trim()}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HR Overview — admin/manager only */}
      {isPrivileged && hrStats && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Employee headcount */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="card-title">HR Overview</span>
              <button onClick={() => navigate('/admin/hr')} style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View HR →</button>
            </div>
            <div className="card-body" style={{ padding: '14px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
                {[
                  { label: 'Active',    value: hrStats.active,    bg: '#d1fae5', color: '#065f46' },
                  { label: 'On Leave',  value: hrStats.on_leave,  bg: '#fef3c7', color: '#92400e' },
                  { label: 'On Notice', value: hrStats.on_notice, bg: '#ffedd5', color: '#c2410c' },
                  { label: 'Total',     value: hrStats.total,     bg: '#eff6ff', color: '#1e40af' },
                ].map(({ label, value, bg, color }) => (
                  <div key={label} style={{ background: bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: '0.7rem', color, opacity: 0.85, fontWeight: 600, marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
              {hrStats.pending_leaves > 0 && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: '0.82rem', color: '#dc2626', fontWeight: 600 }}>
                  {hrStats.pending_leaves} leave request{hrStats.pending_leaves > 1 ? 's' : ''} awaiting approval
                </div>
              )}
              {hrStats.departments_list?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>By Department</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {hrStats.departments_list.slice(0, 6).map(d => (
                      <span key={d.department} style={{ padding: '3px 10px', background: '#f1f5f9', borderRadius: 12, fontSize: '0.72rem', color: '#374151', fontWeight: 600 }}>
                        {d.department} <span style={{ color: '#64748b' }}>({d.count})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payroll summary */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                Payroll — {payrollSummary ? new Date(payrollSummary.month + '-01').toLocaleString('en-IN', { month: 'long', year: 'numeric' }) : '—'}
              </span>
            </div>
            <div className="card-body" style={{ padding: '14px 20px' }}>
              {!payrollSummary ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No payroll data</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 14 }}>
                    {[
                      { label: 'Employees', value: payrollSummary.total, bg: '#eff6ff', color: '#1e40af' },
                      { label: 'Processed',  value: payrollSummary.processed, bg: '#ede9fe', color: '#7c3aed' },
                      { label: 'Paid',       value: payrollSummary.paid, bg: '#d1fae5', color: '#065f46' },
                    ].map(({ label, value, bg, color }) => (
                      <div key={label} style={{ background: bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
                        <div style={{ fontSize: '0.7rem', color, opacity: 0.85, fontWeight: 600, marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {payrollSummary.totalNet > 0 && (
                    <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.82rem', color: '#166534', fontWeight: 600 }}>Total Paid (Net)</span>
                      <span style={{ fontSize: '1rem', fontWeight: 800, color: '#059669' }}>
                        ₹{payrollSummary.totalNet.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                  {payrollSummary.processed < payrollSummary.total && (
                    <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '6px 10px' }}>
                      {payrollSummary.total - payrollSummary.processed} employees not yet processed for this month
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
